import React, { useState, useEffect } from 'react';
import './App.css';
import CodePuzzleGame from './CodePuzzleGame';
import ProblemMenu from './ProblemMenu';
import Login from './Login';
import api from './api';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [targetProblemId, setTargetProblemId] = useState(null);

  useEffect(() => {
    // Check if URL has token parameter (from OAuth callback)
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      console.log('🔑 Token received from OAuth, storing in localStorage');
      localStorage.setItem('authToken', token);
      // Remove token from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // In development mode, skip token check and just call the API
      // The backend will automatically authenticate as jbaramidze@gmail.com
      if (process.env.NODE_ENV !== 'production') {
        console.log('🔧 Dev mode: Skipping token check, using mock auth');
        const response = await api.get('/api/auth/current-user');
        console.log('📥 Dev auth response:', response.data);

        if (response.data.authenticated) {
          setUser(response.data.user);
        }
        setLoading(false);
        return;
      }

      // Production: Check for JWT token
      const token = localStorage.getItem('authToken');
      console.log('🔍 Checking auth... Token present:', !!token);

      if (!token) {
        console.log('❌ No token found');
        setLoading(false);
        return;
      }

      const response = await api.get('/api/auth/current-user');
      console.log('📥 Auth response:', response.data);

      if (response.data.authenticated) {
        setUser(response.data.user);
      } else {
        // Token invalid or expired, remove it
        localStorage.removeItem('authToken');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      if (process.env.NODE_ENV === 'production') {
        localStorage.removeItem('authToken');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.get('/api/auth/logout');
      localStorage.removeItem('authToken');
      setUser(null);
      setSelectedCategory(null);
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if API call fails, clear local token
      localStorage.removeItem('authToken');
      setUser(null);
      setSelectedCategory(null);
    }
  };

  const handleSelectCategory = (category, problemId = null) => {
    setSelectedCategory(category);
    setTargetProblemId(problemId);
  };

  const handleBackToMenu = () => {
    setSelectedCategory(null);
    setTargetProblemId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center">
        <div className="text-white text-2xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  // Show menu if no category is selected
  if (!selectedCategory) {
    return (
      <div>
        <div className="bg-slate-800 border-b border-slate-700 px-6 py-3">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              {user.picture && (
                <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
              )}
              <span className="text-white font-semibold">{user.name}</span>
            </div>
            <button
              onClick={handleLogout}
              className="bg-slate-600 hover:bg-slate-700 text-white px-4 py-2 rounded font-semibold transition"
            >
              Logout
            </button>
          </div>
        </div>
        <ProblemMenu key={Date.now()} onSelectCategory={handleSelectCategory} user={user} />
      </div>
    );
  }

  // Show game when category is selected
  return (
    <div>
      <div className="bg-slate-800 border-b border-slate-700 px-6 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBackToMenu}
              className="text-slate-400 hover:text-white transition"
            >
              ← Back to Menu
            </button>
            <span className="text-slate-600">|</span>
            {user.picture && (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
            )}
            <span className="text-white font-semibold">{user.name}</span>
          </div>
          <button
            onClick={handleLogout}
            className="bg-slate-600 hover:bg-slate-700 text-white px-4 py-2 rounded font-semibold transition"
          >
            Logout
          </button>
        </div>
      </div>
      <CodePuzzleGame
        selectedCategory={selectedCategory}
        targetProblemId={targetProblemId}
        onBackToMenu={handleBackToMenu}
        user={user}
      />
    </div>
  );
}

export default App;
